import { useSelector } from 'react-redux';

import { isInPopUpBrowser } from '@skippay/core/modules/clientInfo/utils';

import { Amount } from '../@types/amount';
import { SanityLanguage } from '../@types/cms';
import { Country } from '../@types/country';
import { Currency } from '../@types/currency';
import { getUserCountry } from '../modules/user';
import { CURRENCY_CZK, CURRENCY_EUR, CURRENCY_HUF } from '../utils/amountUtils';

export const DEFAULT_COUNTRY = 'CZ';

function trimTrailingSlash(url: string): string {
	return url.replace(/\/+$/, '');
}

function getSelfCareUrlFromEnv(country: Country): string {
	if (process.env.NODE_ENV === `development`) {
		return 'http://localhost:8100';
	}

	switch (country) {
		case 'CZ':
			return process.env.SELF_CARE_URL_CS || '';
		case 'SK':
			return process.env.SELF_CARE_URL_SK || '';
		case 'HU':
			return process.env.SELF_CARE_URL_HU || '';
		default:
			return '';
	}
}

export const getSelfCareUrl = (
	country: Country
): {
	inappBrowserUrl: string;
	selfCareUrl: string;
} => {
	const selfCareUrl = getSelfCareUrlFromEnv(country);

	if (process.env.SELF_CARE_URL_CREDENTIALS && isInPopUpBrowser()) {
		// when SELF_CARE_URL_CREDENTIALS is set, insert credentials to the SELF_CARE_URL, but only when we are in pop-up
		// browser. This is needed for iOS do not remember basic auth.
		const selfCareUrlInstance = new URL(selfCareUrl);
		const [username, password] = process.env.SELF_CARE_URL_CREDENTIALS.split(`:`, 2);
		selfCareUrlInstance.username = username;
		selfCareUrlInstance.password = password;

		return {
			inappBrowserUrl: trimTrailingSlash(selfCareUrlInstance.toString()),
			selfCareUrl,
		};
	}

	if (process.env.SELF_CARE_URL_CREDENTIALS) {
		const [scheme, hostname] = selfCareUrl.split(`://`, 2);
		return {
			inappBrowserUrl: `${scheme}://${process.env.SELF_CARE_URL_CREDENTIALS}@${hostname}`,
			selfCareUrl,
		};
	}

	return {
		inappBrowserUrl: selfCareUrl,
		selfCareUrl,
	};
};

export const getPHostname = (country: Country): string | undefined => {
	const { selfCareUrl } = getSelfCareUrl(country);
	if (selfCareUrl) {
		try {
			return new URL(selfCareUrl).hostname;
		} catch (error) {
			const withoutProtocol = selfCareUrl.replace(/^https?:\/\//, '');
			return withoutProtocol.split('/')[0];
		}
	}

	if (typeof window !== 'undefined' && window.location?.hostname) {
		return window.location.hostname;
	}

	return undefined;
};

function getSanityLanguage(country: Country): SanityLanguage {
	switch (country) {
		case 'CZ':
			return SanityLanguage.csCZ;
		case 'SK':
			return SanityLanguage.skSK;
		case 'HU':
			return SanityLanguage.huHU;
		default:
			return SanityLanguage.csCZ;
	}
}

export function getCurrency(country: Country): Currency {
	switch (country) {
		case 'CZ':
			return CURRENCY_CZK;
		case 'SK':
			return CURRENCY_EUR;
		case 'HU':
			return CURRENCY_HUF;
		default:
			return CURRENCY_CZK;
	}
}

function getPromoURL(country: Country): string {
	switch (country) {
		case 'SK':
			return 'https://promo.skippay.cz/sk';
		case 'HU':
			return 'https://promo.skippay.cz/hu';
		default:
			return 'https://promo.skippay.cz';
	}
}

const useUserCountryRelatedInfo = (
	initialCountry?: Country
): {
	country: Country;
	sanityLanguage: SanityLanguage;
	currency: Currency;
	inappBrowserUrl: string;
	selfCareUrl: string;
	deferredPayment: {
		lowLimit: Amount;
	};
	revolving: {
		insufficientIncomeDifference: Amount;
		lowIncomeDifference: Amount;
	};
	installments: {
		defaultMinPrincipal: Amount;
	};
	limitAmendment: {
		insufficientIncomeDifference: Amount;
		lowIncomeDifference: Amount;
	};
	nullAmount: Amount;
	nonPurposeInstallmentsApplication: {
		min: Amount;
		default: Amount;
		max: Amount;
	};
	promoUrl: string;
} => {
	const reduxCountry = useSelector(getUserCountry);
	const country = initialCountry ?? reduxCountry ?? DEFAULT_COUNTRY;
	const isCountryCZ = country === DEFAULT_COUNTRY;
	const sanityLanguage = getSanityLanguage(country);
	const currency = getCurrency(country);

	const { inappBrowserUrl, selfCareUrl } = getSelfCareUrl(country);

	return {
		country,
		sanityLanguage,
		currency,
		inappBrowserUrl,
		selfCareUrl,
		deferredPayment: {
			lowLimit: {
				amount: 500_00,
				currency,
			},
		},
		revolving: {
			insufficientIncomeDifference: {
				amount: isCountryCZ ? 251_00 : 25100,
				currency,
			},
			lowIncomeDifference: {
				amount: isCountryCZ ? 2001_00 : 200100,
				currency,
			},
		},
		limitAmendment: {
			insufficientIncomeDifference: {
				amount: isCountryCZ ? 251_00 : 25100,
				currency,
			},
			lowIncomeDifference: {
				amount: isCountryCZ ? 2001_00 : 200100,
				currency,
			},
		},
		installments: {
			defaultMinPrincipal: {
				amount: 150000,
				currency,
			},
		},
		nullAmount: {
			amount: 0,
			currency,
		},
		nonPurposeInstallmentsApplication: {
			min: {
				amount: isCountryCZ ? 1500_00 : 0,
				currency,
			},
			default: {
				amount: isCountryCZ ? 50000_00 : 0,
				currency,
			},
			max: {
				amount: isCountryCZ ? 100000_00 : 0,
				currency,
			},
		},
		promoUrl: getPromoURL(country),
	};
};

export default useUserCountryRelatedInfo;
